/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import SignalPopup from '../../heart-rate-sessions/SignalPopup';
import { showTimeInSec, showTimeFromSeconds, formatISODateToLocal } from '../../../utils/utils';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q } from './kit';

const LIMIT = 20;
const deviceLabel = (v) => (v === 'true' ? 'Watch' : v === 'false' ? 'Mobile' : 'Other');

// One paginated list — breath or meditation — mirroring the /breath-list and /pauses-list
// pages' columns, scoped to this user + the shared date window.
const PauseList = ({ kind, userId, from, to, authGet }) => {
  const [page, setPage] = useState(1);
  const [popup, setPopup] = useState(null);
  useEffect(() => { setPage(1); }, [userId, from, to, kind]);

  const ep = kind === 'breath' ? 'breath' : 'pauses';
  const { status, data, reload } = useTabData(async () => {
    const res = await authGet(`/admin/api/${ep}?${q(userId, from, to, { page, limit: LIMIT })}`);
    return { rows: res.data.data || [], total: res.data.totalItem || 0 };
  }, [userId, from, to, kind, page]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;
  const { rows, total } = data;
  if (!rows.length && page === 1) {
    return (
      <TabEmpty
        title={kind === 'breath' ? 'No breathing pauses' : 'No meditation pauses'}
        message="Nothing recorded for this user in the selected window."
        icon="❋"
      />
    );
  }

  const SignalCell = ({ r }) => (
    <td data-label="Signal Details">
      <button
        type="button"
        className="au-link"
        aria-label={`View signal details for record ${r.id}`}
        onClick={() => setPopup(r.dump_hr_details ?? null)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        View
      </button>
    </td>
  );

  return (
    <>
      <div className="au-table-wrap">
        <table className="au-table" data-testid={`ud-${kind}-table`}>
          {kind === 'breath' ? (
            <thead>
              <tr>
                <th>No</th><th>Name</th><th>Perform time</th><th>Breath time</th><th>Breath type</th>
                <th>Breath pattern</th><th>Breath cycle</th><th>Breath cycle length</th>
                <th>Inhale</th><th>Exhale</th><th>Hold</th><th>From Device</th><th>Created</th><th>Signal Details</th>
              </tr>
            </thead>
          ) : (
            <thead>
              <tr>
                <th>No</th><th>Name</th><th>Perform time</th><th>Meditation time</th><th>Meditation type</th><th>Created</th><th>Signal Details</th>
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r, i) => (kind === 'breath' ? (
              <tr key={r.id}>
                <td data-label="No" className="tnum">{(page - 1) * LIMIT + i + 1}</td>
                <td data-label="Name" className="au-ellipsis">{r.name}</td>
                <td data-label="Perform time" className="tnum">{showTimeInSec(r.breath_perform_time)}</td>
                <td data-label="Breath time" className="tnum">{showTimeInSec(r.breath_time)}</td>
                <td data-label="Breath type">{r.breath_type}</td>
                <td data-label="Breath pattern">{r.breath_pattern}</td>
                <td data-label="Breath cycle" className="tnum">{r.breath_cycle}</td>
                <td data-label="Breath cycle length" className="tnum">{r.breath_cycle_length}</td>
                <td data-label="Inhale" className="tnum">{showTimeInSec(r.breath_inhale)}</td>
                <td data-label="Exhale" className="tnum">{showTimeInSec(r.breath_exhale)}</td>
                <td data-label="Hold" className="tnum">{showTimeInSec(r.breath_hold)}</td>
                <td data-label="From Device">{deviceLabel(r.is_from_watch)}</td>
                <td data-label="Created">{formatISODateToLocal(r.timestamp_utc)}</td>
                <SignalCell r={r} />
              </tr>
            ) : (
              <tr key={r.id}>
                <td data-label="No" className="tnum">{(page - 1) * LIMIT + i + 1}</td>
                <td data-label="Name" className="au-ellipsis">{r.name}</td>
                <td data-label="Perform time" className="tnum">{showTimeInSec(r.meditation_perform_time)}</td>
                <td data-label="Meditation time" className="tnum">{showTimeFromSeconds(r.meditation_time)}</td>
                <td data-label="Meditation type">{r.meditation_type}</td>
                <td data-label="Created">{formatISODateToLocal(r.timestamp_utc)}</td>
                <SignalCell r={r} />
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div className="ud-pager">
          <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={total} pageRangeDisplayed={5} onChange={setPage} />
        </div>
      )}
      {popup != null && <SignalPopup data={popup} onClose={() => setPopup(null)} />}
    </>
  );
};

// Pause records tab = two sub-tabs (Breath / Meditation), each an inline paginated list
// mirroring the /breath-list and /pauses-list pages.
const PauseRecordsTab = ({ userId, from, to, authGet }) => {
  const [sub, setSub] = useState('breath');
  return (
    <div data-testid="ud-pause-records">
      <div className="ud-subtabs" role="tablist" aria-label="Pause type">
        <button
          type="button" role="tab" aria-selected={sub === 'breath'}
          className={`ud-subtab${sub === 'breath' ? ' ud-subtab--on' : ''}`}
          data-testid="ud-subtab-breath" onClick={() => setSub('breath')}
        >
          Breath
        </button>
        <button
          type="button" role="tab" aria-selected={sub === 'meditation'}
          className={`ud-subtab${sub === 'meditation' ? ' ud-subtab--on' : ''}`}
          data-testid="ud-subtab-meditation" onClick={() => setSub('meditation')}
        >
          Meditation
        </button>
      </div>
      <div className="au-panel" style={{ paddingTop: 4 }}>
        <PauseList key={sub} kind={sub} userId={userId} from={from} to={to} authGet={authGet} />
      </div>
    </div>
  );
};

export default PauseRecordsTab;
